import { useEffect, useRef, useState } from "react";
import api, { fmtMoney, fmtDate, apiErr, API_BASE } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Building2, Phone, Mail, Globe, FileText, Upload, Trash2, ShoppingCart, Wallet } from "lucide-react";
import { toast } from "sonner";

function Row({ label, value }) {
  if (!value) return null;
  return <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm"><span className="text-slate-400">{label}</span><span className="font-medium text-slate-700 text-right">{value}</span></div>;
}

export default function SupplierDetailDialog({ supplierId, onOpenChange }) {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => { if (supplierId) api.get(`/suppliers/${supplierId}`).then((r) => setData(r.data)).catch(() => setData({ error: true })); };
  useEffect(() => { setData(null); load(); }, [supplierId]);

  const s = data?.supplier;
  const onFile = async (e) => {
    const f = e.target.files[0]; e.target.value = ""; if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/suppliers/${supplierId}/documents?type=Document`, fd); toast.success("Document ajouté"); load(); }
    catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };
  const viewDoc = (d) => { const token = localStorage.getItem("omnibat_token"); fetch(`${API_BASE}/suppliers/${supplierId}/documents/${d.id}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b))); };
  const delDoc = async (d) => { if (!window.confirm("Supprimer ce document ?")) return; await api.delete(`/suppliers/${supplierId}/documents/${d.id}`); toast.success("Supprimé"); load(); };

  return (
    <Dialog open={!!supplierId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="supplier-detail">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Building2 className="h-5 w-5 text-brand-600" />{s ? `${s.numero} · ${s.nom}` : "Fiche fournisseur"}</DialogTitle></DialogHeader>
        {!data ? <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-500" /></div> : data.error ? <p className="py-8 text-center text-slate-400">Introuvable.</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border bg-slate-50 p-3"><p className="text-[10px] uppercase font-mono text-slate-400">Total achats</p><p className="font-mono font-bold text-slate-800">{fmtMoney(data.total_achats)}</p></div>
              <div className="rounded-lg border bg-slate-50 p-3"><p className="text-[10px] uppercase font-mono text-slate-400">Payé</p><p className="font-mono font-bold text-emerald-600">{fmtMoney(data.total_paye)}</p></div>
              <div className="rounded-lg border bg-slate-50 p-3"><p className="text-[10px] uppercase font-mono text-slate-400">Dette</p><p className="font-mono font-bold text-rose-600">{fmtMoney(data.dette)}</p></div>
              <div className="rounded-lg border bg-slate-50 p-3"><p className="text-[10px] uppercase font-mono text-slate-400">Articles fournis</p><p className="font-mono font-bold text-slate-800">{data.total_articles}</p></div>
            </div>

            <Tabs defaultValue="infos">
              <TabsList>
                <TabsTrigger value="infos" data-testid="sup-tab-infos">Général</TabsTrigger>
                <TabsTrigger value="commercial" data-testid="sup-tab-commercial">Commercial</TabsTrigger>
                <TabsTrigger value="historique" data-testid="sup-tab-historique">Historique ({data.purchases.length})</TabsTrigger>
                <TabsTrigger value="documents" data-testid="sup-tab-documents">Documents ({(s.documents || []).length})</TabsTrigger>
              </TabsList>
              <TabsContent value="infos">
                <div className="rounded-lg border bg-white p-4">
                  <Row label="Type" value={s.type} /><Row label="Contact" value={s.contact_nom} />
                  <Row label="Téléphone" value={s.telephone} /><Row label="E-mail" value={s.email} />
                  <Row label="Site web" value={s.site_web} /><Row label="Adresse" value={[s.adresse, s.ville, s.pays].filter(Boolean).join(", ")} />
                  <Row label="NIF" value={s.nif} /><Row label="RCCM" value={s.rccm} />
                </div>
              </TabsContent>
              <TabsContent value="commercial">
                <div className="rounded-lg border bg-white p-4">
                  <Row label="Catégories fournies" value={s.categories} /><Row label="Conditions commerciales" value={s.conditions_commerciales} />
                  <Row label="Conditions de paiement" value={s.conditions_paiement} /><Row label="Délais de livraison" value={s.delais_livraison} />
                  <Row label="Devise" value={s.devise} /><Row label="Remises" value={s.remises} /><Row label="Observations" value={s.observations} />
                </div>
              </TabsContent>
              <TabsContent value="historique">
                <div className="rounded-lg border bg-white overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b"><ShoppingCart className="h-4 w-4 text-brand-600" /><span className="text-sm font-semibold">Achats</span></div>
                  {data.purchases.length === 0 ? <p className="px-4 py-3 text-sm text-slate-400">Aucun achat.</p> : data.purchases.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 text-sm border-b last:border-0">
                      <span className="font-mono text-xs text-slate-500 w-28">{p.numero}</span>
                      <span className="text-xs text-slate-500 w-24">{fmtDate(p.date)}</span>
                      <StatusBadge value={p.dette_etat} />
                      <span className="ml-auto font-mono text-xs font-bold flex items-center gap-1"><Wallet className="h-3 w-3 text-slate-400" />{fmtMoney(p.total)}</span>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="documents">
                <div className="flex justify-end mb-3">
                  <input ref={fileRef} type="file" hidden onChange={onFile} data-testid="supplier-doc-input" />
                  <Button size="sm" disabled={uploading} onClick={() => fileRef.current.click()} data-testid="supplier-doc-upload" className="bg-brand-600 hover:bg-brand-700 text-white">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" />Ajouter un document</>}</Button>
                </div>
                {(s.documents || []).length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Aucun document (RCCM, NIF, contrats, catalogues…).</p> : (
                  <div className="space-y-2">
                    {(s.documents || []).map((d) => (
                      <div key={d.id} className="flex items-center gap-3 rounded-lg border bg-white px-3 py-2 text-sm">
                        <FileText className="h-4 w-4 text-brand-500" /><span className="flex-1 truncate">{d.nom}</span>
                        <span className="text-xs text-slate-400">{fmtDate(d.created_at)}</span>
                        <Button size="sm" variant="ghost" onClick={() => viewDoc(d)} data-testid={`supplier-doc-view-${d.id}`}>Voir</Button>
                        <Button size="icon" variant="ghost" onClick={() => delDoc(d)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
